import React from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Box, Typography } from '@mui/material';
import { makeStyles, withStyles } from 'tss-react/mui';
import { ButtonStyleCheckboxGroup, ButtonStyleCheckbox, Spinner } from '@uplift/ui-v2';
import { RematchingContainer } from '../components/RematchingContainer';
import { FormFooter } from '../components/FormFooter';
import { useSpecialties } from '@uplift/queries';
import { WhatNextData } from '../state-machine/machine';
import { ProviderMedicalRole } from '@uplift/types';
import { useL10n } from '@uplift/hooks';


const useRematchingPageStyles = makeStyles()({
  picker: {
    width: '100%',
  }
});


const StyledButtonStyleCheckboxGroup = withStyles(ButtonStyleCheckboxGroup, theme => ({
  items: {
    [theme.breakpoints.up('sm')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      columnGap: theme.spacing(2),
    }
  }
}));

const useCheckboxClasses = makeStyles()({
  label: {
    fontSize: '15px',
  }
});


const validationSchema = Yup.object().shape({
  specialtyIds: Yup.array(Yup.string()).min(1).required(),
});

const REASON_NOT_HERE = 'reason_not_here';

export type RematchingWhatPageProps = {
  onBack: () => void;
  onNext: (data: WhatNextData) => void;
  specialtyIds: string[];
  medicalRole: ProviderMedicalRole;
};

export const RematchingWhatPage: React.VFC<RematchingWhatPageProps> = props => {
  const { classes: checkboxClasses } = useCheckboxClasses();
  const { classes } = useRematchingPageStyles();
  const l10n = useL10n();
  const roleLabel = l10n.resources.provider.roles[props.medicalRole].toLowerCase();
  const { data: specialties = [], isInitialLoading } = useSpecialties();

  return (
    <RematchingContainer>
      <Typography variant="h3">What do you want to discuss with<br/>your {roleLabel}?</Typography>
      <Box mt={2}>
        <Typography variant="h4">We’ll only show you {roleLabel}s who specialize in those areas.</Typography>
      </Box>
      {isInitialLoading ? (
        <Box mt={20}>
          <Spinner />
        </Box>
      ) : (
        <Formik
          initialValues={{ specialtyIds: props.specialtyIds }}
          enableReinitialize
          validateOnMount
          validationSchema={validationSchema}
          onSubmit={v => {
            if (v.specialtyIds.includes(REASON_NOT_HERE)) {
              props.onNext({ specialtyIds: [] });
              return;
            }

            props.onNext({ specialtyIds: v.specialtyIds });
          }}
        >
          {formik => (
            <>
              <Box mt={4}>
                <StyledButtonStyleCheckboxGroup color="secondary" label="Select all that apply" className={classes.picker} name="specialtyIds">
                  {specialties.concat([ {
                    id: REASON_NOT_HERE,
                    name: 'My reason isn\'t here',
                  } ]).map((item) => (
                    <ButtonStyleCheckbox
                      key={item.id}
                      value={item.id}
                      label={item.name}
                      controlClasses={checkboxClasses}
                      disabled={formik.values.specialtyIds.includes(REASON_NOT_HERE) && item.id !== REASON_NOT_HERE}
                    />
                  )
                  )}
                </StyledButtonStyleCheckboxGroup>
              </Box>
              <FormFooter
                nextDisabled={!formik.isValid}
                onBack={props.onBack}
                onNext={formik.handleSubmit}
              />
            </>
          )}
        </Formik>
      )}
    </RematchingContainer>
  );
};
